import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Search,
    Plus,
    MoreHorizontal,
    Calendar,
    User,
    Edit,
    Trash2,
    Loader2,
    Eye,
    CheckCircle,
    FileText,
    Clock,
    ArrowLeft
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { blogStatusOptions } from '@/constants';
import { getAllBlogPosts, deleteBlogPost, updateBlogStatus, BlogPost } from '@/services/blog';
import { toast } from '@/hooks/use-toast';
import { PaginationControl } from '@/components/shared/PaginationControl';
import { pagination } from '@/constants';
import { capitalizeWords } from '@/helper';

const BlogManagement = () => {
    const navigate = useNavigate();
    const [posts, setPosts] = useState<BlogPost[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [postToDelete, setPostToDelete] = useState<string | null>(null);
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const [totalPages, setTotalPages] = useState(1);

    // Debounce search query
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [searchQuery]);

    const fetchPosts = async (page = pagination.page) => {
        setIsLoading(true);
        try {
            const { data, pagination: paginationData } = await getAllBlogPosts({
                page: page,
                limit: pagination.limit,
                searchQuery: debouncedSearchQuery,
                status: statusFilter !== 'all' ? statusFilter : undefined
            });

            if (Array.isArray(data)) {
                setPosts(data);
                if (paginationData) {
                    setTotalPages(paginationData.totalPages);
                }
            } else {
                console.error("Unexpected API response format:", data);
            }
        } catch (error) {
            console.error("Error fetching blog posts:", error);
            toast({
                title: "Error",
                description: "Failed to fetch blog posts.",
                variant: "destructive"
            });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPosts();
    }, [debouncedSearchQuery, statusFilter]);

    const handleCreateClick = () => {
        navigate('/admin/blog/create');
    };

    const handleEditClick = (slug: string) => {
        navigate(`/admin/blog/edit/${slug}`);
    };

    const handleViewClick = (slug: string) => {
        navigate(`/blogs/${slug}`,);
    };

    const handleStatusUpdate = async (slug: string, status: 'draft' | 'published') => {
        try {
            await updateBlogStatus(slug, status);
            toast({
                title: "Success",
                description: `Blog post marked as ${status}.`,
            });
            fetchPosts();
        } catch (error: any) {
            console.error("Error updating blog status:", error);
            toast({
                title: "Error",
                description: error.response?.data?.error || "Failed to update blog status.",
                variant: "destructive"
            });
        }
    };

    const confirmDelete = (slug: string) => {
        setPostToDelete(slug);
        setDeleteDialogOpen(true);
    };

    const handleDelete = async () => {
        if (!postToDelete) return;

        try {
            await deleteBlogPost(postToDelete);
            toast({
                title: "Success",
                description: "Blog post deleted successfully.",
            });
            fetchPosts();
        } catch (error) {
            console.error("Error deleting blog post:", error);
            toast({
                title: "Error",
                description: "Failed to delete blog post.",
                variant: "destructive"
            });
        } finally {
            setDeleteDialogOpen(false);
            setPostToDelete(null);
        }
    };



    const statusColors = {
        published: 'bg-green-100 text-green-800 hover:bg-green-100',
        draft: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
    };

    return (
        <div className="min-h-screen flex flex-col">
            <Header />

            <div className="pt-32 pb-20 bg-secondary/30 flex-1">
                <div className="container mx-auto px-4 md:px-6">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
                        <div>
                            <h1 className="text-3xl md:text-4xl font-bold">Blog Management</h1>
                            <p className="text-muted-foreground">Create and manage content for the blog</p>
                        </div>
                        <div className="flex gap-2">
                            <Button variant="outline" asChild>
                                <Link to="/admin/dashboard">
                                    <ArrowLeft className="h-4 w-4 mr-2" />
                                    Back to Dashboard
                                </Link>
                            </Button>
                            <Button onClick={handleCreateClick}>
                                <Plus className="h-4 w-4 mr-2" />
                                <span className="hidden sm:inline">Create New Post</span>
                                <span className="sm:hidden">Create</span>
                            </Button>
                        </div>
                    </div>

                    <Card className="mb-8">
                        <CardHeader className="pb-3">
                            <CardTitle>Filters</CardTitle>
                            <CardDescription>Search and filter blog posts</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="flex flex-col md:flex-row gap-4">
                                <div className="relative flex-1">
                                    <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <Input
                                        type="text"
                                        placeholder="Search by title or category..."
                                        className="pl-8"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </div>
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-full md:w-[200px]">
                                        <SelectValue placeholder="Filter by status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All</SelectItem>
                                        {blogStatusOptions.map(option => (
                                            <SelectItem key={option.value} value={option.value}>
                                                {option.label}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardContent>
                    </Card>

                    <div className="rounded-md border bg-card">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Title</TableHead>
                                    <TableHead className="hidden md:table-cell">Author</TableHead>
                                    <TableHead className="hidden md:table-cell">Category</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="hidden md:table-cell">Date</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    [1, 2, 3, 4, 5].map((i) => (
                                        <TableRow key={i}>
                                            <TableCell><Skeleton className="h-4 w-64" /></TableCell>
                                            <TableCell className="hidden md:table-cell">
                                                <div className="flex items-center gap-2">
                                                    <Skeleton className="h-3 w-3 rounded-full" />
                                                    <Skeleton className="h-3 w-20" />
                                                </div>
                                            </TableCell>
                                            <TableCell className="hidden md:table-cell"><Skeleton className="h-4 w-24" /></TableCell>
                                            <TableCell><Skeleton className="h-6 w-20 rounded-full" /></TableCell>
                                            <TableCell className="hidden md:table-cell">
                                                <div className="flex items-center gap-2">
                                                    <Skeleton className="h-3 w-3 rounded-full" />
                                                    <Skeleton className="h-3 w-24" />
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right"><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                                        </TableRow>
                                    ))
                                ) : posts.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="h-24 text-center">
                                            No blog posts found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    posts.map((post) => (
                                        <TableRow key={post._id}>
                                            <TableCell className="font-medium max-w-[200px] md:max-w-[300px] truncate" title={post.title}>
                                                {capitalizeWords(post.title)}
                                            </TableCell>
                                            <TableCell className="hidden md:table-cell">
                                                <div className="flex items-center text-sm">
                                                    <User className="h-3 w-3 mr-2 text-muted-foreground" />
                                                    {capitalizeWords(post.author)}
                                                </div>
                                            </TableCell>
                                            <TableCell className="hidden md:table-cell">{capitalizeWords(post.category)}</TableCell>
                                            <TableCell>
                                                <Badge className={statusColors[post.status] || 'bg-gray-100'}>
                                                    {capitalizeWords(post.status)}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="hidden md:table-cell">
                                                <div className="flex items-center text-sm">
                                                    <Calendar className="h-3 w-3 mr-2 text-muted-foreground" />
                                                    {post.publishDate ? new Date(post.publishDate).toLocaleDateString() : 'N/A'}
                                                </div>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="sm">
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuLabel>Actions</DropdownMenuLabel>
                                                        <DropdownMenuItem onClick={() => post.slug && handleViewClick(post.slug)}>
                                                            <Eye className="h-4 w-4 mr-2" />
                                                            View
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem onClick={() => post.slug && handleEditClick(post.slug)}>
                                                            <Edit className="h-4 w-4 mr-2" />
                                                            Edit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuSeparator />
                                                        {post.status !== 'published' && (
                                                            <DropdownMenuItem onClick={() => post.slug && handleStatusUpdate(post.slug, 'published')}>
                                                                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                                                Mark as Published
                                                            </DropdownMenuItem>
                                                        )}
                                                        {post.status !== 'draft' && (
                                                            <DropdownMenuItem onClick={() => post.slug && handleStatusUpdate(post.slug, 'draft')}>
                                                                <FileText className="h-4 w-4 mr-2 text-gray-600" />
                                                                Mark as Draft
                                                            </DropdownMenuItem>
                                                        )}
                                                        <DropdownMenuSeparator />
                                                        <DropdownMenuItem className="text-red-600" onClick={() => post.slug && confirmDelete(post.slug)}>
                                                            <Trash2 className="h-4 w-4 mr-2" />
                                                            Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    {/* Pagination */}
                    <div className="flex justify-center mt-4">
                        <PaginationControl
                            key={`${debouncedSearchQuery}-${statusFilter}`} // Reset on filter change
                            totalPages={totalPages}
                            onPageChange={fetchPosts}
                        />
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Confirm Deletion</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete this blog post? This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialogOpen(false)}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                        >
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Footer />
        </div>
    );
};

export default BlogManagement;
